import { apiClient } from './apiClient';
import {
  DeviceSettings,
  DeviceSettingsUpdate,
  DeviceRebootRequest,
  DeviceRebootResponse,
  DeviceRule,
} from '@/types/api';

/** One working mode of a control device, described from the backend catalog. */
export interface WorkingMode {
  mode: number;
  label: string;
  /** What this mode offers, derived from its terminals — never hardcoded here. */
  summary: string;
  offers: Record<string, string[]>;
  /** Input terminals this mode drops relative to the current one. */
  loses: string[];
  /** Of those, the ones already BOUND to something: what would actually break. */
  breaking: string[];
  is_current: boolean;
  is_recommended: boolean;
  selectable: boolean;
  not_selectable_reason: string | null;
  /** Caveats worth showing, e.g. a 16-bit counter that wraps. */
  notes: string[];
}

export interface WorkingModesResponse {
  device_id: number;
  device_type: string | null;
  /** False for hardware with no working-mode concept (Opta, UC300). */
  supported: boolean;
  current: number | null;
  target: number | null;
  matches_target: boolean;
  label?: string;
  modes: WorkingMode[];
  /**
   * Why `current` is null, when it is. The mode is learned from the device's
   * UPLINKS, so a publish-only process can never learn it however many downlinks
   * it sends — the case where a change is published successfully and the reported
   * mode stays empty forever.
   */
  current_unknown_reason?: string | null;
}

export interface WorkingModeResult {
  message: string;
  mode: number;
  payload_hex: string;
  note: string;
  breaking: string[];
}

export interface SensorDownlinkLastCommand {
  command_type: string;
  interval_seconds: number | null;
  status: string;
  created_at: string | null;
  sent_at: string | null;
  acked_at: string | null;
}

export interface SensorDownlinkConfig {
  dev_eui: string;
  manufacturer: string | null;
  supports_config: boolean;
  supports_reboot: boolean;
  min_interval_seconds: number;
  max_interval_seconds: number;
  // Step the interval input should snap to (e.g. 60 s for minute-granular
  // SenseCAP devices). Optional; falls back to a default in the UI.
  interval_step_seconds?: number | null;
  observed_interval_seconds: number | null;
  last_command: SensorDownlinkLastCommand | null;
  // The last set-reporting-interval command specifically (not clobbered by a
  // later reboot/other command), so the UI can default the input to the value
  // we last sent to the device.
  last_interval_command: SensorDownlinkLastCommand | null;
  // Device info decoded from the raw payload (optional; present for sensors that
  // report it, e.g. SenseCAP).
  firmware_version?: string | null;
  hardware_version?: string | null;
  battery_percent?: number | null;
  battery_updated_at?: string | null;
  measure_interval_seconds?: number | null;
  gps_interval_seconds?: number | null;
  model_id?: string | null;

  // ── Dragino SW3L flow triggers ──────────────────────────────────────
  // `supports_flow_triggers` is model-only (is this an SW3L?), so the panel
  // stays visible on a counter that is still awaiting its firmware upgrade.
  // `has_extended_flow_triggers` is the firmware check — false means the
  // command must be sent with `confirm`, and it is also false when the counter
  // has simply never sent a device-status frame, so it is a "not confirmed"
  // rather than a "known too old".
  supports_flow_triggers?: boolean;
  has_extended_flow_triggers?: boolean;
  minimum_flow_trigger_firmware?: string | null;
  // Pulses per litre for FTRIG's parameter a. A SITE setting (the meter body
  // fitted), NOT a device reading — `calculate_flag` is an operator-set tag and
  // reads 0 across this fleet, so it identifies no K-factor.
  pulses_per_litre?: number | null;
  // The operator-set tag itself, so a meter where somebody DID run AT+CALCFLAG
  // is visible rather than silently inheriting the site assumption.
  calculate_flag?: number | null;
  // Device-reported readback of the PTRIG parameters actually in force (fPort 4).
  stop_duration_seconds?: number | null;
  alarm_timer_minutes?: number | null;
  last_flow_trigger?: SensorFlowTriggerParams | null;
  last_pulse_trigger?: SensorPulseTriggerParams | null;
}

export interface SensorFlowTriggerParams {
  pulses_per_litre: number;
  trigger_quantity: number;
  min_interval_minutes: number;
  reset_after_upload: boolean;
  status: string;
}

export interface SensorPulseTriggerParams {
  stop_duration_seconds: number;
  alarm_timer: number;
  alarm_unit: number;
  report_on_flow_stop: boolean;
  status: string;
}

export interface SensorFlowTriggerRequest {
  pulses_per_litre: number;
  trigger_quantity: number;
  min_interval_minutes: number;
  reset_after_upload: boolean;
  confirm?: boolean;
}

export interface SensorPulseTriggerRequest {
  stop_duration_seconds: number;
  alarm_timer: number;
  alarm_unit: number;
  report_on_flow_stop: boolean;
  confirm?: boolean;
}

export interface SensorDownlinkCommandResult {
  command_id: number;
  payload_hex: string;
  status: string;
  deduplicated?: boolean;
}

class DeviceSettingsApiService {
  /**
   * Get device settings
   */
  async getDeviceSettings(devEui: string): Promise<DeviceSettings> {
    return apiClient.get<DeviceSettings>(`/devices/${devEui}/settings`);
  }

  /**
   * Update device settings
   */
  async updateDeviceSettings(
    devEui: string, 
    settings: DeviceSettingsUpdate
  ): Promise<DeviceSettings> {
    return apiClient.put<DeviceSettings>(`/devices/${devEui}/settings`, settings);
  }

  /**
   * Reboot device
   */
  async rebootDevice(
    devEui: string, 
    confirmation: string
  ): Promise<DeviceRebootResponse> {
    return apiClient.post<DeviceRebootResponse>(`/devices/${devEui}/reboot`, {
      confirmation,
    } as DeviceRebootRequest);
  }

  /**
   * Get the Dragino downlink config (manufacturer, supports flags, observed
   * interval, last-command status) for a TTN sensor.
   */
  async getSensorDownlinkConfig(devEui: string): Promise<SensorDownlinkConfig> {
    return apiClient.get<SensorDownlinkConfig>(`/sensor-data/downlink/${devEui}/config`);
  }

  /**
   * Queue a Dragino set-reporting-interval downlink.
   */
  async setSensorReportingInterval(
    devEui: string,
    intervalSeconds: number
  ): Promise<SensorDownlinkCommandResult> {
    return apiClient.post<SensorDownlinkCommandResult>(
      `/sensor-data/downlink/${devEui}/reporting-interval`,
      { interval_seconds: intervalSeconds }
    );
  }

  /**
   * The device's working modes, what each offers, and what switching would cost.
   *
   * On a Dragino LT-22222-L the mode decides which terminals report AT ALL and the
   * modes are mutually exclusive, so this is not a preference — it is what the box
   * can measure. All the wording comes from the backend catalog so it cannot drift
   * from the actual terminal layout.
   */
  async getWorkingModes(devEui: string): Promise<WorkingModesResponse> {
    return apiClient.get<WorkingModesResponse>(
      `/devices/${devEui}/config/working-mode`
    );
  }

  /**
   * Queue a working-mode change. Rejected with 409 (detail.breaking) when bound
   * terminals would go quiet, unless `confirm` is set.
   */
  async setWorkingMode(
    devEui: string, mode: number, confirm = false
  ): Promise<WorkingModeResult> {
    return apiClient.post<WorkingModeResult>(
      `/devices/${devEui}/config/working-mode`,
      { mode, confirm }
    );
  }

  /**
   * Queue an AT+FTRIG downlink: make a Dragino SW3L counter report every N
   * litres instead of on a fixed clock.
   *
   * Rejected with 409 (`detail.requires_confirmation`) when the counter's
   * reported firmware predates the extended command set. Re-send with
   * `confirm: true` to queue it anyway.
   */
  async setSensorFlowTrigger(
    devEui: string,
    body: SensorFlowTriggerRequest
  ): Promise<SensorDownlinkCommandResult> {
    return apiClient.post<SensorDownlinkCommandResult>(
      `/sensor-data/downlink/${devEui}/flow-trigger`,
      body
    );
  }

  /**
   * Queue an AT+PTRIG downlink: flow-stop detection and the water-waste alarm.
   * Same 409-then-confirm contract as `setSensorFlowTrigger`.
   */
  async setSensorPulseTrigger(
    devEui: string,
    body: SensorPulseTriggerRequest
  ): Promise<SensorDownlinkCommandResult> {
    return apiClient.post<SensorDownlinkCommandResult>(
      `/sensor-data/downlink/${devEui}/pulse-trigger`,
      body
    );
  }

  /**
   * Queue a Dragino reboot/reset downlink.
   */
  async rebootSensor(devEui: string): Promise<SensorDownlinkCommandResult> {
    return apiClient.post<SensorDownlinkCommandResult>(
      `/sensor-data/downlink/${devEui}/reboot`,
      {}
    );
  }

  /**
   * Get all 16 rule slots for a device
   */
  async getDeviceRules(devEui: string): Promise<DeviceRule[]> {
    return apiClient.get<DeviceRule[]>(`/devices/${devEui}/rules`);
  }

  /**
   * Trigger manual sync of all rules from device
   */
  async syncDeviceRules(devEui: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(`/devices/${devEui}/rules/sync`);
  }

  /**
   * Delete a single rule slot from device
   */
  async deleteDeviceRule(devEui: string, ruleId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/devices/${devEui}/rules/${ruleId}`);
  }

  /**
   * Delete all rules from device
   */
  async deleteAllDeviceRules(devEui: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/devices/${devEui}/rules`);
  }
}

// Create and export singleton instance
export const deviceSettingsApi = new DeviceSettingsApiService();

// Export class for testing purposes
export { DeviceSettingsApiService };

export default deviceSettingsApi;